import { useState } from 'react'
import {Smartphone, Bot, Watch, Laptop, ArrowUpRight, CheckCircle2} from 'lucide-react'

type Hub = {
  id: string
  badge: string
  title: string
  Icon: typeof Smartphone
  keywords: string[]
  steps: { title: string; detail: string }[]
}

const HUBS: Hub[] = [
  {
    id: 'iphone',
    badge: 'iOS · Apple',
    title: 'iPhone & iOS',
    Icon: Smartphone,
    keywords: ['extend battery life on iphone', 'battery saver mode on iphone'],
    steps: [
      { title: 'Enable Low Power Mode at 80%', detail: 'Settings → Battery → Low Power Mode. Toggle proactively, not at 20%.' },
      { title: 'Disable Always-On Display', detail: 'Settings → Display & Brightness → Always On. Saves 5–8% per day on Pro models.' },
      { title: 'Audit Background App Refresh', detail: 'Settings → General → Background App Refresh → Wi-Fi only.' },
      { title: 'Switch to Dark Mode (OLED)', detail: 'Settings → Display → Appearance → Dark. Up to 15% gain on OLED panels.' },
      { title: 'Trim Location Services', detail: 'Settings → Privacy → Location Services → While Using only, except essentials.' },
    ],
  },
  {
    id: 'android',
    badge: 'Android · Samsung',
    title: 'Android & Samsung',
    Icon: Bot,
    keywords: ['android power saving mode', 'battery saver app for android'],
    steps: [
      { title: 'Activate Adaptive Battery', detail: 'Settings → Battery → Adaptive preferences. ML throttles unused apps.' },
      { title: 'Enable Power Saving Mode', detail: 'Quick Settings → Power saving. Caps CPU + background sync.' },
      { title: 'Tighten Screen Timeout', detail: 'Settings → Display → Screen timeout → 30 seconds.' },
      { title: 'Restrict Always-On Display', detail: 'Settings → Lock screen → AOD → Tap to show, 10s.' },
      { title: 'Cut Bloatware Background Use', detail: 'Settings → Apps → [App] → Battery → Restricted.' },
    ],
  },
  {
    id: 'watch',
    badge: 'Wearables · OLED',
    title: 'Smart Wearables',
    Icon: Watch,
    keywords: ['apple watch save battery', 'apple watch settings to save battery'],
    steps: [
      { title: 'Disable Always-On Display', detail: 'Watch app → Display & Brightness → Always On → Off.' },
      { title: 'Reduce Wake Duration', detail: 'Settings → Display → Wake Duration → Wake for 15 sec.' },
      { title: 'Turn Off Wrist Raise', detail: 'Settings → Display → Wake on Wrist Raise → Off when conserving.' },
      { title: 'Limit Background App Refresh', detail: 'Watch → General → Background App Refresh → Off.' },
      { title: 'Use Low Power Mode (watchOS 9+)', detail: 'Control Center → Battery icon → Low Power Mode.' },
    ],
  },
  {
    id: 'laptop',
    badge: 'macOS · Windows',
    title: 'Laptops & Systems',
    Icon: Laptop,
    keywords: ['laptop power saver mode', 'windows battery saver'],
    steps: [
      { title: 'Engage Low Power Mode (macOS)', detail: 'System Settings → Battery → Low Power Mode → Always.' },
      { title: 'Activate Battery Saver (Windows)', detail: 'Settings → System → Power & battery → Battery saver → 50%.' },
      { title: 'Cap Display Brightness at 70%', detail: 'Most panels are calibrated to 100%, costing disproportionate watts.' },
      { title: 'Quit Browser Tab Hogs', detail: 'Use Activity Monitor / Task Manager — close anything &gt;5% CPU idle.' },
      { title: 'Disable Bluetooth & Keyboard Backlight', detail: 'Reclaim 4–7% by killing peripherals you aren’t actively using.' },
    ],
  },
]

function DeviceHubs() {
  const [activeId, setActiveId] = useState(HUBS[0].id)
  const active = HUBS.find((h) => h.id === activeId)!

  return (
    <section id="hubs" className="relative py-24 lg:py-32 bg-slate-900 text-bone">
      <div className="absolute inset-0 grain opacity-20 pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-8 mb-14 items-end">
          <div className="lg:col-span-7">
            <div className="font-mono text-[10.5px] uppercase tracking-[0.24em] text-amber-400 mb-4" style={{ color: '#FCD34D' }}>§ 02 · Field Manuals</div>
            <h2 className="font-display text-[clamp(2rem,4.5vw,3.75rem)] leading-[1] font-medium" style={{ color: '#FAF8F5' }}>
              Device-specific <br className="hidden lg:block" />
              <span className="italic font-light text-amber-300">optimization blueprints.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pl-8">
            <p className="text-slate-400 text-[15.5px] leading-[1.7]">
              Step-by-step routines distilled from manufacturer guidance, hardware reviewer benchmarks, and thousands of real-world battery telemetry hours.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Selector */}
          <div className="lg:col-span-4 space-y-2">
            {HUBS.map((h) => {
              const isOn = h.id === activeId
              return (
                <button
                  key={h.id}
                  onClick={() => setActiveId(h.id)}
                  className={`w-full text-left rounded-2xl p-5 border transition-all duration-300 ${
                    isOn
                      ? 'bg-amber-500/10 border-amber-400/40'
                      : 'bg-white/[0.02] border-white/[0.06] hover:bg-white/[0.04] hover:border-white/[0.12]'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      isOn ? 'bg-amber-500/20' : 'bg-white/[0.05]'
                    }`}>
                      <h.Icon className={`w-5 h-5 ${isOn ? 'text-amber-300' : 'text-slate-300'}`} strokeWidth={1.8} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5">{h.badge}</div>
                      <div className="font-display text-[20px] font-medium" style={{ color: '#FAF8F5' }}>{h.title}</div>
                    </div>
                    <ArrowUpRight className={`w-4 h-4 mt-1 transition ${isOn ? 'text-amber-300' : 'text-slate-600'}`} />
                  </div>
                </button>
              )
            })}
          </div>

          {/* Detail */}
          <div className="lg:col-span-8 rounded-3xl bg-bone text-slate-900 p-8 lg:p-10 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-amber-200/30 blur-3xl rounded-full pointer-events-none" />
            <div className="relative">
              <div className="flex items-start justify-between gap-4 mb-8 pb-6 border-b border-slate-900/10">
                <div>
                  <div className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-amber-700 mb-2">{active.badge}</div>
                  <h3 className="font-display text-3xl lg:text-4xl font-medium text-slate-900">{active.title}</h3>
                </div>
                <div className="hidden sm:flex flex-col items-end gap-1.5">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Targeted queries</div>
                  {active.keywords.map((k) => (
                    <div key={k} className="font-mono text-[11px] text-slate-700 px-2 py-0.5 bg-slate-900/[0.05] rounded">/{k}</div>
                  ))}
                </div>
              </div>

              <ol className="space-y-5">
                {active.steps.map((step, i) => (
                  <li key={i} className="flex gap-5 group">
                    <div className="flex-shrink-0 flex flex-col items-center">
                      <div className="w-9 h-9 rounded-full bg-slate-900 text-bone flex items-center justify-center font-mono text-[12px] font-medium" style={{ color: '#FAF8F5' }}>
                        {String(i + 1).padStart(2, '0')}
                      </div>
                      {i < active.steps.length - 1 && <div className="w-px flex-1 bg-slate-900/15 my-2 min-h-[12px]" />}
                    </div>
                    <div className="pb-2 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-display text-[19px] font-medium text-slate-900 leading-snug">{step.title}</h4>
                        <CheckCircle2 className="w-4 h-4 text-amber-600 opacity-0 group-hover:opacity-100 transition" />
                      </div>
                      <p className="text-slate-600 text-[14px] mt-1.5 leading-relaxed" dangerouslySetInnerHTML={{ __html: step.detail }} />
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default DeviceHubs
